import { RecordCollection } from '../../app/record-collection.js';
import { matchesListSearch } from '../../lib/list-search.js';
import { HEALTH_THEME_COLORS, type HealthThemeColor } from '@hue/contracts';
import {
  Button,
  ChoiceChips,
  FormErrorSummary,
  IconButton,
  Input,
  Modal,
  StatusPill,
  useSnackbar,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, PencilLine, Trash2, Archive, RotateCcw } from 'lucide-react';
import { useId, useState } from 'react';
import { getApiError, recordsApi, type RecordHealthTheme } from '../../lib/api.js';
import { formatDate } from '../../lib/time-zone.js';
import { ListFilterBar, ErrorCard, LoadingCard, RequestAlert } from '../../app/data-view.js';
import { ThemeTag, Toggle } from './shared.js';
import { cn, themeColors } from './helpers.js';
import { CreateButton, PageHeader } from '../../app/page-title.js';

const queryKey = ['company', 'records', 'themes'] as const;

/// Temas de saúde (ADR 0038, decisão 7): a base do sistema, travada porque o sistema age sobre
/// ela, e os temas que a empresa cria para juntar no Quadro o que é do seu negócio.
export function HealthThemes({ timeZone }: { timeZone: string }) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const themes = useQuery({ queryKey, queryFn: recordsApi.listThemes, retry: false });
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<RecordHealthTheme | 'new'>();
  const [removing, setRemoving] = useState<RecordHealthTheme>();
  const [error, setError] = useState<string>();

  async function refresh(message: string) {
    await queryClient.invalidateQueries({ queryKey: ['company', 'records'] });
    snackbar.success(message);
  }

  async function toggle(theme: RecordHealthTheme) {
    setError(undefined);
    try {
      await recordsApi.setThemeActive(theme.key, !theme.active);
      await refresh(theme.active ? 'Tema desativado.' : 'Tema reativado.');
    } catch (failure) {
      setError(getApiError(failure));
    }
  }

  async function remove(theme: RecordHealthTheme) {
    setError(undefined);
    try {
      await recordsApi.deleteTheme(theme.key);
      setRemoving(undefined);
      await refresh('Tema excluído.');
    } catch (failure) {
      setError(getApiError(failure));
    }
  }

  const visible = (themes.data ?? []).filter((theme) =>
    matchesListSearch(theme.displayName, search),
  );
  function themeActions(theme: RecordHealthTheme) {
    return (
      <div className="flex justify-end gap-1">
        {theme.system ? (
          <span className="text-xs text-content-faint">Travado</span>
        ) : (
          <>
            <IconButton
              label={`Editar ${theme.displayName}`}
              icon={<PencilLine className="size-4" />}
              onClick={() => setEditing(theme)}
            />
            <IconButton
              label={
                theme.active ? `Desativar ${theme.displayName}` : `Reativar ${theme.displayName}`
              }
              icon={theme.active ? <Archive /> : <RotateCcw />}
              tone={theme.active ? 'danger' : 'neutral'}
              onClick={() => void toggle(theme)}
            />
            {theme.inUse ? null : (
              <IconButton
                tone="danger"
                label={`Excluir ${theme.displayName}`}
                icon={<Trash2 className="size-4" />}
                onClick={() => {
                  setError(undefined);
                  setRemoving(theme);
                }}
              />
            )}
          </>
        )}
      </div>
    );
  }
  return (
    <section aria-labelledby="health-themes-title">
      <PageHeader
        trail="Configurações / Temas de saúde"
        id="health-themes-title"
        title="Temas de saúde"
        description={
          <>
            Os temas ligam perguntas de fichas diferentes: é assim que a alergia declarada na ficha
            de um serviço aparece no Quadro de saúde de todos. Os do sistema vêm prontos e ficam
            travados, porque o sistema age sobre eles.
          </>
        }
        actions={
          <>
            <CreateButton onClick={() => setEditing('new')}>Novo tema</CreateButton>
          </>
        }
      />
      <ListFilterBar
        searchLabel="Buscar temas"
        search={search}
        onSearchChange={setSearch}
        count={`${visible.length} ${visible.length === 1 ? 'tema' : 'temas'}`}
      />
      <div className="grid gap-6">
        <DivergenceExplained />

        {error && !removing ? <RequestAlert message={error} /> : null}

        {themes.isLoading ? (
          <LoadingCard label="Carregando temas" />
        ) : themes.error || !themes.data ? (
          <ErrorCard message={getApiError(themes.error)} />
        ) : (
          <RecordCollection
            label="Temas de saúde"
            records={visible}
            recordKey={(theme) => theme.key}
            columns={[
              {
                key: 'name',
                label: 'Tema',
                value: (theme) => theme.displayName,
                render: (theme) => (
                  <div className="grid gap-1">
                    <ThemeTag theme={theme} />
                    <span className="text-xs text-content-muted">
                      {theme.flagsDivergence
                        ? 'Avisa divergências'
                        : 'Só soma no Quadro, sem comparar Sim e Não'}
                    </span>
                  </div>
                ),
              },
              {
                key: 'origin',
                label: 'Origem',
                value: (theme) => Number(theme.system),
                render: (theme) => (
                  <div>
                    <StatusPill tone={theme.system ? 'neutral' : 'brand'}>
                      {theme.system ? 'Do sistema' : 'Da empresa'}
                    </StatusPill>
                    {theme.createdByDisplayName && theme.createdAt ? (
                      <p className="text-xs text-content-muted">
                        criado por {theme.createdByDisplayName} em{' '}
                        {formatDate(theme.createdAt, timeZone)}
                      </p>
                    ) : null}
                  </div>
                ),
              },
              {
                key: 'usage',
                label: 'Perguntas em uso',
                value: (theme) => theme.usage.length,
                render: (theme) => (
                  <span className="text-xs text-content-muted">
                    {theme.usage.length
                      ? `${String(theme.usage.length)} ${theme.usage.length === 1 ? 'pergunta' : 'perguntas'}: ${theme.usage
                          .map((use) => `${use.templateName}: ${use.questionLabel}`)
                          .join('; ')}`
                      : 'Nenhuma pergunta em uso'}
                  </span>
                ),
              },
              {
                key: 'status',
                label: 'Situação',
                value: (theme) => Number(theme.active),
                render: (theme) => (
                  <StatusPill tone={theme.active ? 'positive' : 'warning'}>
                    {theme.active ? 'Ativo' : 'Desativado'}
                  </StatusPill>
                ),
              },
            ]}
            actions={themeActions}
            card={(theme) => (
              <article
                key={theme.key}
                className="grid gap-3 px-5 py-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_7rem] md:items-center"
              >
                <div className="grid gap-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <ThemeTag theme={theme} />
                    {theme.system ? (
                      <StatusPill tone="neutral">Do sistema</StatusPill>
                    ) : (
                      <StatusPill tone="brand">Da empresa</StatusPill>
                    )}
                    {theme.active ? null : <StatusPill tone="warning">Desativado</StatusPill>}
                  </div>
                  <p className="text-xs text-content-muted">
                    {theme.flagsDivergence
                      ? 'Avisa divergências'
                      : 'Só soma no Quadro, sem comparar Sim e Não'}
                    {theme.createdByDisplayName && theme.createdAt
                      ? ` · criado por ${theme.createdByDisplayName} em ${formatDate(theme.createdAt, timeZone)}`
                      : ''}
                  </p>
                </div>
                <p className="text-xs text-content-muted">
                  {theme.usage.length
                    ? `${String(theme.usage.length)} ${theme.usage.length === 1 ? 'pergunta' : 'perguntas'}: ${theme.usage
                        .map((use) => `${use.templateName}: ${use.questionLabel}`)
                        .join('; ')}`
                    : 'Nenhuma pergunta em uso'}
                </p>
                {themeActions(theme)}
              </article>
            )}
          />
        )}

        <p className="text-xs text-content-faint">
          Desativar tira o tema da lista do construtor; perguntas já ligadas continuam alimentando o
          Quadro. Tema em uso não é excluído, e o nome novo vale para todo o histórico.
        </p>

        {editing ? (
          <ThemeDialog
            theme={editing === 'new' ? null : editing}
            onClose={() => setEditing(undefined)}
            onSaved={async (created) => {
              setEditing(undefined);
              await refresh(created ? 'Tema criado.' : 'Tema atualizado.');
            }}
          />
        ) : null}
        {removing ? (
          <Modal
            open
            onOpenChange={() => setRemoving(undefined)}
            title="Excluir tema de saúde"
            description="Nenhuma pergunta usa este tema. Excluir não pode ser desfeito."
            footer={
              <>
                <Button variant="outline" onClick={() => setRemoving(undefined)}>
                  Cancelar
                </Button>
                <Button onClick={() => void remove(removing)}>Excluir tema</Button>
              </>
            }
          >
            <p className="text-sm">
              Excluir <strong>{removing.displayName}</strong>?
            </p>
            {error ? <RequestAlert message={error} /> : null}
          </Modal>
        ) : null}
      </div>
    </section>
  );
}

function ThemeDialog({
  theme,
  onClose,
  onSaved,
}: {
  theme: RecordHealthTheme | null;
  onClose: () => void;
  onSaved: (created: boolean) => Promise<void>;
}) {
  const formId = useId();
  const [displayName, setDisplayName] = useState(theme?.displayName ?? '');
  const [color, setColor] = useState<HealthThemeColor>(theme?.color ?? 'brand');
  const [flagsDivergence, setFlagsDivergence] = useState(theme?.flagsDivergence ?? false);
  const [nameError, setNameError] = useState<string>();
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);

  async function submit() {
    setError(undefined);
    const name = displayName.trim();
    if (name.length < 2 || name.length > 60) {
      setNameError('Use de 2 a 60 caracteres no nome do tema.');
      return;
    }
    setSaving(true);
    try {
      const input = { displayName: name, color, flagsDivergence };
      if (theme) await recordsApi.updateTheme(theme.key, input);
      else await recordsApi.createTheme(input);
      await onSaved(!theme);
    } catch (failure) {
      setError(getApiError(failure));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
      title={theme ? 'Editar tema de saúde' : 'Novo tema de saúde'}
      footer={
        <>
          <FormErrorSummary form={formId} className="mr-auto" />
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" form={formId} loading={saving}>
            {theme ? 'Salvar tema' : 'Criar tema'}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        noValidate
        className="grid gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Input
          label="Nome do tema"
          value={displayName}
          placeholder="Ex.: Cicatrização, Queda de cabelo"
          error={nameError}
          onChange={(event) => {
            setDisplayName(event.target.value);
            setNameError(undefined);
          }}
        />
        <div className="grid gap-2">
          <span className="text-xs font-semibold text-content/70">Cor da etiqueta</span>
          <ChoiceChips
            label="Cor da etiqueta"
            placeholder="Buscar cor"
            value={color}
            options={HEALTH_THEME_COLORS.map((value) => ({ value, label: themeColors[value] }))}
            onChange={(value) => setColor(value as HealthThemeColor)}
          />
          <div>
            <ThemeTag theme={{ displayName: displayName.trim() || 'Prévia', color }} />
          </div>
        </div>
        <div className="grid gap-1.5">
          <Toggle checked={flagsDivergence} onChange={setFlagsDivergence}>
            Avisar divergências (um Não numa ficha contra um Sim em outra)
          </Toggle>
          <p className="text-xs leading-5 text-content-muted">
            Ligue só quando as perguntas deste tema forem equivalentes em todas as fichas. Se elas
            variam (“asma?” e “hipotireoidismo?”), um Não não contradiz o Sim.
          </p>
        </div>
        {error ? <RequestAlert message={error} /> : null}
      </form>
    </Modal>
  );
}

/// A divergência compara temas, não perguntas: é o que mais surpreende quem monta a ficha, e o que
/// decide se um tema novo deve ou não avisar divergências. Fica recolhida: é consulta, não aviso.
function DivergenceExplained() {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  return (
    <section className="rounded-2xl border border-info/30 bg-info/10 text-sm leading-6">
      <h2>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((value) => !value)}
          className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-2xl px-5 py-3 text-left font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
        >
          Divergências
          <ChevronDown
            aria-hidden="true"
            className={cn('size-4 shrink-0 transition-transform', open && 'rotate-180')}
          />
        </button>
      </h2>
      <div id={bodyId} hidden={!open} className="grid gap-2 px-5 pb-4">
        <p>
          A comparação é pelo <strong>tema</strong> ligado à pergunta, não pelo texto da pergunta.
          Se uma pergunta de sim ou não de uma ficha responde Sim e outra, de outra ficha, responde
          Não, e as duas estão no mesmo tema com o aviso ligado, o Quadro de saúde mostra a
          divergência — mesmo que as perguntas sejam escritas de jeitos diferentes.
        </p>
        <p>
          <strong>Exemplo:</strong> na ficha Saúde geral, “Tem alguma alergia?” foi respondida Não.
          Na ficha Coloração, “Já teve reação a algum produto de tintura?” foi respondida Sim, com o
          detalhe “amônia”. As duas perguntas estão no tema Alergia, então o Quadro mostra a
          divergência, e o profissional confirma com o cliente qual ficha atualizar.
        </p>
        <p className="text-content-muted">
          Por isso cada tema deve falar de um fato só. Gestação e Amamentação são temas separados:
          um Não em “Está amamentando?” não contradiz um Sim em “Está gestante?”. Num tema da
          empresa que junte perguntas sobre fatos diferentes, deixe o aviso desligado.
        </p>
      </div>
    </section>
  );
}
